import { firebaseAction } from '../utils'
import { userSelector, authSelector } from '../selectors/baseSelectors'
import { OWNER, CREATED } from '../../constants'
import { push } from 'react-router-redux'
import ReactGA from 'react-ga'
import randomColor from '../../utils/randomColor'
import { accessListSelector } from '../selectors/accessSelectors'
import i18n from 'i18n'
import { setEditingItem, setSidebarFilter } from './sidebar'
import { confirm } from './alerts'
import firebase from 'firebase/app'
import getUrl from '../../utils/getUrl';

const timestamp = firebase.database.ServerValue.TIMESTAMP

export const createFolder = parent =>
  firebaseAction(async (update, { getState, getKey, dispatch }) => {
    const state = getState()
    const user = userSelector(state)
    const id = getKey()
    const name = i18n.t('globalNav.newFolderName')
    const color = randomColor(name)
    const folder = {
      type: 'folder',
      id,
      name,
      color,
      dateCreated: timestamp,
      owner: {
        id: user.uid,
        displayName: user.displayName
      }
    }
    const forward = {
      // Folder
      [`/folders/${id}`]: folder,
      // Entry in user's accessList,
      [`/access/${user.uid}/${folder.id}`]: {
        id,
        name,
        color,
        isOwner: true,
        // large number will be top (order is descending), increase with date so newset is first
        order: new Date().valueOf() - 1494110302577,
        type: 'folder',
        accessLevel: OWNER,
        closed: false,
        dateCreated: timestamp,
        dateLastViewed: timestamp
      }
    }

    if (parent) {
      parent = accessListSelector(state)[parent]
      forward[`/folders/${parent.id}/children/${folder.id}`] = {
        id: folder.id,
        name: folder.name,
        type: 'folder'
      }
      forward[`/access/${user.uid}/${folder.id}`].parent = parent.id
      folder.folders = []
      while (parent) {
        folder.folders.push({
          id: parent.id,
          name: parent.name,
          color: parent.color
        })
        parent = parent.parent && accessListSelector(state)[parent.parent]
      }
    }

    await update({ forward })
    dispatch(setSidebarFilter(CREATED))
    dispatch(setEditingItem(folder.id))
    dispatch(push(getUrl(folder)))
    ReactGA.event({ category: 'Folder', action: 'Create Folder' }, ['app'])
    return id
  })

export const updateViewDate = folderId =>
  firebaseAction((update, { getState }) => {
    const state = getState()

    const accessList = accessListSelector(state)
    if (
      !accessList ||
      !(folderId in accessList) ||
      !accessList[folderId].id ||
      !accessList[folderId].accessLevel
    )
      return

    const auth = authSelector(state)
    if (!auth.isAnonymous) {
      const path = `/access/${auth.uid}/${folderId}/dateLastViewed`
      const forward = {
        [path]: timestamp
      }
      return update({ forward })
    }
  })

export const archiveFolder = folder =>
  firebaseAction((update, { getAuth, dispatch }) => {
    const action = () => {
      const forward = {
        [`/folders/${folder.id}/dateArchived`]: timestamp,
        // delete from accessList for faster UI update
        [`/access/${getAuth().uid}/${folder.id}/dateArchived`]: timestamp
      }
      update({ forward })
      ReactGA.event({ category: 'Folder', action: 'Archive Folder' }, ['app'])
    }

    dispatch(confirm('globalNav.confirmArchive', action))
  })

export const unarchiveFolder = folder =>
  firebaseAction((update, { getAuth, getState }) => {
    const auth = getAuth()
    const forward = {
      [`/folders/${folder.id}/dateArchived`]: null,
      // remove from my accessList for faster UI update
      [`/access/${auth.uid}/${folder.id}/dateArchived`]: null
    }
    if (folder.parent) {
      const accessList = accessListSelector(getState())
      const parent = accessList[folder.parent]
      if (!parent || parent.dateArchived)
        forward[`/folders/${folder.id}/folders`] = null
    }
    ReactGA.event({ category: 'Folder', action: 'Archive Folder' }, ['app'])
    return update({ forward })
  })

export const deleteFolder = folder =>
  firebaseAction((update, { getAuth }) => {
    const forward = {
      [`/folders/${folder.id}`]: null,
      // delete from accessList for faster UI update
      [`/access/${getAuth().uid}/${folder.id}`]: null
    }
    if (folder.folders) {
      forward[`/folders/${folder.folders[0].id}/children/${folder.id}`] = null
    }
    update({ forward })
    ReactGA.event({ category: 'Folder', action: 'Delete Folder' }, ['app'])
  })

export const updateFolder = (folder, data) =>
  firebaseAction((update, { getAuth }) => {
    const forward = {}
    Object.keys(data).forEach(key => {
      forward[`/folders/${folder.id}/${key}`] = data[key]
    })

    // update my own accessList to make interface update faster
    const treePath = `/access/${getAuth().uid}/${folder.id}/`
    if ('name' in data) {
      forward[treePath + 'name/'] = data.name
    }
    if ('color' in data) {
      forward[treePath + 'color/'] = data.color
    }

    return update({ forward })
  })
